import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { getAllUsers } from "../../../api/api";
import { getProjectProgressProjects } from "../../../api/projectProgressApi";
import catPhoto from "../../../images/cat-photo.png";
import "./ProjectsSchedule.css";

const getOrderNumber = (project) =>
  project?.cipher_pilot || project?.contract_number || "";

const ORDER_SELECT_STYLES = {
  control: (provided) => ({
    ...provided,
    minHeight: 32,
    border: "1px solid #c5d0c8",
    borderRadius: 8,
    background: "#fff",
    boxShadow: "none",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 8px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#1f2937",
    fontWeight: 600,
    fontSize: 13,
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "0 8px",
    color: "#6b7280",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 20,
    borderRadius: 8,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: 13,
    backgroundColor: state.isSelected
      ? "#2a5e38"
      : state.isFocused
        ? "#e6f2ea"
        : "white",
    color: state.isSelected ? "#fff" : "#1f2937",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#e6f2ea",
    borderRadius: 4,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    fontSize: 12,
    color: "#1f2937",
  }),
};

const BUILDING_TYPES = ["МЖК", "ОЗ", "ИЖД", "НП16"];

const STATUS_OPTIONS = [
  "Завершен",
  "Закрытие",
  "Приостановлен",
  "Разработка",
  "Экспертиза",
  "Передан в АН",
  "Корректировка",
  "Перспективный",
];

const STATUS_COLORS = {
  Завершен: "#22a06b",
  Завершено: "#22a06b",
  Закрытие: "#e67e22",
  Приостановлен: "#9aaa3a",
  Разработка: "#3b82f6",
  Экспертиза: "#e74c3c",
  "Передан в АН": "#1f2937",
  "Авторский надзор": "#1f2937",
  Корректировка: "#0d7377",
  Перспективный: "#8b5cf6",
  Отказ: "#6b7280",
};

const EMPTY_DATE = "00.00.0000";

const formatDate = (value) => {
  if (!value) return EMPTY_DATE;
  const str = String(value).trim();
  if (!str || str.startsWith("0000") || str === "null") return EMPTY_DATE;

  // DD.MM.YYYY already
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(str)) return str;

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;

  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return EMPTY_DATE;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const extractYear = (value) => {
  if (!value) return null;
  const str = String(value);
  const iso = str.match(/^(\d{4})/);
  if (iso) return iso[1];
  const dmy = str.match(/\.(\d{4})$/);
  if (dmy) return dmy[1];
  return null;
};

const getChiefName = (chief) => {
  if (!chief) return "—";
  return `${chief.surname || ""} ${chief.name || ""}`.trim() || "—";
};

const normalizePersonName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/\s+/g, " ")
    .trim();

const personNameKeys = (person) => {
  if (!person) return [];
  const surname = person.surname || person.lastName || "";
  const name = person.name || person.firstName || "";
  return [
    ...new Set(
      [
        normalizePersonName(`${surname} ${name}`),
        normalizePersonName(`${name} ${surname}`),
        normalizePersonName(person.full_name),
        normalizePersonName(getChiefName(person)),
      ].filter(Boolean),
    ),
  ];
};

const buildChiefPhotoMaps = (users) => {
  const byId = {};
  const byName = {};
  (Array.isArray(users) ? users : []).forEach((user) => {
    const image = user?.image;
    if (!image) return;
    if (user.id != null) byId[Number(user.id)] = image;
    personNameKeys(user).forEach((key) => {
      if (!byName[key]) byName[key] = image;
    });
  });
  return { byId, byName };
};

const resolveChiefImage = (chief, photos) => {
  if (chief?.image) return chief.image;
  if (!chief || !photos) return null;
  if (chief.id != null && photos.byId[Number(chief.id)]) {
    return photos.byId[Number(chief.id)];
  }
  for (const key of personNameKeys(chief)) {
    if (photos.byName[key]) return photos.byName[key];
  }
  return null;
};

const ChiefAvatar = ({ chief, photos, className = "ps-chief-avatar" }) => (
  <img
    src={resolveChiefImage(chief, photos) || catPhoto}
    alt=""
    className={className}
    onError={(e) => {
      e.currentTarget.src = catPhoto;
    }}
  />
);

const getStatusColor = (status) => STATUS_COLORS[status] || "#6b7280";

const SortIcon = ({ active, direction }) => (
  <span
    className={`ps-sort-icon${active ? " ps-sort-icon--active" : ""}`}
    aria-hidden
  >
    {active ? (direction === "asc" ? "↑" : "↓") : "↕"}
  </span>
);

const CheckboxOption = ({ checked, onChange, children, className = "" }) => (
  <label className={`ps-check ${className}`.trim()}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="ps-check__box" aria-hidden />
    <span className="ps-check__label">{children}</span>
  </label>
);

const ProjectsSchedule = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [years, setYears] = useState([]);
  const [chiefs, setChiefs] = useState([]);
  const [chiefPhotos, setChiefPhotos] = useState({ byId: {}, byName: {} });

  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedBuildingTypes, setSelectedBuildingTypes] = useState([]);
  const [selectedChiefIds, setSelectedChiefIds] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState([]);

  const [sortKey, setSortKey] = useState("contract_year");
  const [sortDir, setSortDir] = useState("desc");

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [projectsResult, usersResult] = await Promise.allSettled([
        getProjectProgressProjects({ exclude: "ЭП" }),
        getAllUsers(),
      ]);

      if (projectsResult.status === "rejected") {
        throw projectsResult.reason;
      }

      const data = projectsResult.value;
      const list = Array.isArray(data?.project_portfolio)
        ? data.project_portfolio
        : Array.isArray(data)
          ? data
          : [];
      setProjects(list);

      const photos =
        usersResult.status === "fulfilled"
          ? buildChiefPhotoMaps(usersResult.value)
          : { byId: {}, byName: {} };
      setChiefPhotos(photos);

      const yearSet = new Set();
      const chiefMap = new Map();
      list.forEach((p) => {
        const y = extractYear(p.contract_year) || extractYear(p.date_start);
        if (y) yearSet.add(y);
        if (p.chief?.id != null) {
          chiefMap.set(p.chief.id, {
            id: p.chief.id,
            name: getChiefName(p.chief),
            chief: p.chief,
            image: resolveChiefImage(p.chief, photos),
          });
        }
      });

      const sortedYears = Array.from(yearSet).sort((a, b) => Number(b) - Number(a));
      setYears(sortedYears);
      setChiefs(Array.from(chiefMap.values()).sort((a, b) => a.name.localeCompare(b.name, "ru")));
    } catch (e) {
      setError(e?.message || "Не удалось загрузить проекты");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const toggleMulti = (value, setSelected) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const orderNumberOptions = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => {
      const code = getOrderNumber(p);
      if (code) set.add(code);
    });
    return Array.from(set)
      .sort((a, b) => String(a).localeCompare(String(b), "ru", { numeric: true }))
      .map((value) => ({ value, label: value }));
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const year = extractYear(p.contract_year) || extractYear(p.date_start);
      const yearOk =
        selectedYears.length === 0 || (year && selectedYears.includes(year));
      const typeOk =
        selectedBuildingTypes.length === 0 ||
        selectedBuildingTypes.includes(p.building_type);
      const chiefOk =
        selectedChiefIds.length === 0 ||
        (p.chief?.id != null && selectedChiefIds.includes(p.chief.id));
      const statusOk =
        selectedStatuses.length === 0 || selectedStatuses.includes(p.status);
      const orderNumber = getOrderNumber(p);
      const orderOk =
        selectedOrderNumbers.length === 0 ||
        selectedOrderNumbers.includes(orderNumber);
      return yearOk && typeOk && chiefOk && statusOk && orderOk;
    });
  }, [
    projects,
    selectedYears,
    selectedBuildingTypes,
    selectedChiefIds,
    selectedStatuses,
    selectedOrderNumbers,
  ]);

  const sortedProjects = useMemo(() => {
    const list = [...filteredProjects];
    const dir = sortDir === "asc" ? 1 : -1;

    list.sort((a, b) => {
      const getVal = (p) => {
        switch (sortKey) {
          case "contract_year":
            return p.contract_year || p.date_start || "";
          case "building_type":
            return p.building_type || "";
          case "cipher_pilot":
            return getOrderNumber(p);
          case "name":
            return p.name || "";
          case "chief":
            return getChiefName(p.chief);
          case "status":
            return p.status || "";
          case "completion_percent":
            return Number(p.completion_percent) || 0;
          default:
            return "";
        }
      };

      const va = getVal(a);
      const vb = getVal(b);

      if (typeof va === "number" && typeof vb === "number") {
        return (va - vb) * dir;
      }
      return String(va).localeCompare(String(vb), "ru") * dir;
    });

    return list;
  }, [filteredProjects, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "contract_year" || key === "completion_percent" ? "desc" : "asc");
    }
  };

  const dateRange = (start, end) => `${formatDate(start)}-${formatDate(end)}`;

  return (
    <div className="ps-page">
      <div className="ps-filters">
        <div className="ps-filters__row">
          <span className="ps-filters__label">Год</span>
          <div className="ps-filters__options">
            <CheckboxOption
              checked={selectedYears.length === 0}
              onChange={() => setSelectedYears([])}
            >
              Все
            </CheckboxOption>
            {years.map((year) => (
              <CheckboxOption
                key={year}
                checked={selectedYears.includes(year)}
                onChange={() => toggleMulti(year, setSelectedYears)}
              >
                {year}
              </CheckboxOption>
            ))}
          </div>
        </div>

        <div className="ps-filters__row">
          <span className="ps-filters__label">Вид здания</span>
          <div className="ps-filters__options">
            <CheckboxOption
              checked={selectedBuildingTypes.length === 0}
              onChange={() => setSelectedBuildingTypes([])}
            >
              Все
            </CheckboxOption>
            {BUILDING_TYPES.map((type) => (
              <CheckboxOption
                key={type}
                checked={selectedBuildingTypes.includes(type)}
                onChange={() => toggleMulti(type, setSelectedBuildingTypes)}
              >
                {type}
              </CheckboxOption>
            ))}
          </div>
        </div>

        <div className="ps-filters__row">
          <span className="ps-filters__label">ГИП</span>
          <div className="ps-filters__options">
            <CheckboxOption
              checked={selectedChiefIds.length === 0}
              onChange={() => setSelectedChiefIds([])}
            >
              Все
            </CheckboxOption>
            {chiefs.map((chief) => (
              <CheckboxOption
                key={chief.id}
                checked={selectedChiefIds.includes(chief.id)}
                onChange={() => toggleMulti(chief.id, setSelectedChiefIds)}
                className="ps-check--chief"
              >
                <ChiefAvatar
                  chief={chief.chief}
                  photos={chiefPhotos}
                  className="ps-chief-avatar ps-chief-avatar--sm"
                />
                {chief.name}
              </CheckboxOption>
            ))}
          </div>
        </div>

        <div className="ps-filters__row">
          <span className="ps-filters__label">Стадия</span>
          <div className="ps-filters__options">
            <CheckboxOption
              checked={selectedStatuses.length === 0}
              onChange={() => setSelectedStatuses([])}
            >
              Все
            </CheckboxOption>
            {STATUS_OPTIONS.map((status) => (
              <CheckboxOption
                key={status}
                checked={selectedStatuses.includes(status)}
                onChange={() => toggleMulti(status, setSelectedStatuses)}
                className="ps-check--status"
              >
                <span
                  className="ps-status-pill"
                  style={{
                    color: getStatusColor(status),
                    borderColor: getStatusColor(status),
                  }}
                >
                  {status}
                </span>
              </CheckboxOption>
            ))}
          </div>
        </div>
      </div>

      <div className="ps-table-card">
        {loading ? (
          <div className="ps-state">Загрузка...</div>
        ) : error ? (
          <div className="ps-state ps-state--error">{error}</div>
        ) : (
          <div className="ps-table-wrap">
            <table className="ps-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="ps-th-btn" onClick={() => handleSort("contract_year")}>
                      Период договора
                      <SortIcon active={sortKey === "contract_year"} direction={sortDir} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className="ps-th-btn" onClick={() => handleSort("building_type")}>
                      Вид здания
                      <SortIcon active={sortKey === "building_type"} direction={sortDir} />
                    </button>
                  </th>
                  <th className="ps-th-order">
                    <Select
                      className="ps-order-select"
                      classNamePrefix="ps-order-select"
                      isMulti
                      isClearable
                      isSearchable
                      placeholder="№ Заказа"
                      noOptionsMessage={() => "Нет номеров"}
                      options={orderNumberOptions}
                      value={orderNumberOptions.filter((opt) =>
                        selectedOrderNumbers.includes(opt.value),
                      )}
                      onChange={(selected) =>
                        setSelectedOrderNumbers(
                          (selected || []).map((opt) => opt.value),
                        )
                      }
                      styles={ORDER_SELECT_STYLES}
                      menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                      }
                      menuPosition="fixed"
                    />
                  </th>
                  <th>Наименование объекта</th>
                  <th>
                    <button type="button" className="ps-th-btn" onClick={() => handleSort("chief")}>
                      ГИП проекта
                      <SortIcon active={sortKey === "chief"} direction={sortDir} />
                    </button>
                  </th>
                  <th>
                    <button type="button" className="ps-th-btn" onClick={() => handleSort("status")}>
                      Стадия
                      <SortIcon active={sortKey === "status"} direction={sortDir} />
                    </button>
                  </th>
                  <th>Сроки</th>
                  <th>
                    <button type="button" className="ps-th-btn" onClick={() => handleSort("completion_percent")}>
                      % Завершения
                      <SortIcon active={sortKey === "completion_percent"} direction={sortDir} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="ps-state">
                      Проекты не найдены
                    </td>
                  </tr>
                ) : (
                  sortedProjects.map((project) => {
                    const percent = Math.max(
                      0,
                      Math.min(100, Number(project.completion_percent) || 0),
                    );
                    const statusColor = getStatusColor(project.status);

                    return (
                      <tr
                        key={project.id}
                        className="ps-table__row--clickable"
                        onClick={() => navigate(`/projects/schedule/${project.id}`)}
                      >
                        <td>{formatDate(project.contract_year)}</td>
                        <td>{project.building_type || "—"}</td>
                        <td>{getOrderNumber(project) || "—"}</td>
                        <td className="ps-cell-name">{project.name || "—"}</td>
                        <td>
                          <div className="ps-chief-cell">
                            <ChiefAvatar chief={project.chief} photos={chiefPhotos} />
                            <span>{getChiefName(project.chief)}</span>
                          </div>
                        </td>
                        <td>
                          {project.status ? (
                            <span
                              className="ps-status-pill"
                              style={{
                                color: statusColor,
                                borderColor: statusColor,
                              }}
                            >
                              {project.status}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="ps-dates">
                          <div className="ps-dates__plan">
                            План: {dateRange(project.plan_start_date, project.date_end)}
                          </div>
                          <div className="ps-dates__fact">
                            Факт: {dateRange(project.fact_start_date, null)}
                          </div>
                        </td>
                        <td>
                          <div className="ps-progress-block">
                            <div className="ps-progress">
                              <div
                                className="ps-progress__fill"
                                style={{ width: `${percent}%` }}
                              />
                              <span className="ps-progress__text">{percent}%</span>
                            </div>
                            <button
                              type="button"
                              className="ps-schedule-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/schedule/${project.id}`);
                              }}
                            >
                              График
                              <span className="ps-schedule-link__icon" aria-hidden>
                                ↗
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsSchedule;
